import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

/**
 * MapWidget - Renders a world map with bubble markers for geographic data
 * Uses react-simple-maps (free, no API key needed)
 * 
 * @param {Array} data - Array of data points with xLabel (location), yValue (amount), and metadata (lat/lng)
 * @param {Object} config - Widget configuration (title, description, etc.)
 * @param {Number} height - Chart height in pixels
 */
export default function MapWidget({ data, config, height = 300 }) {
  const [tooltipContent, setTooltipContent] = useState('');

  // Find min/max values for marker sizing and color
  const values = data.map(item => parseFloat(item.yValue));
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);

  // GeoJSON URL for world map
  const geoUrl = '/countries-110m.json';

  return (
    <Box position="relative">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          center: [0, 20],
          scale: 120,
        }}
        width={800}
        height={450}
        style={{ width: '100%', height: `${height}px` }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#E0E0E0"
                stroke="#FFFFFF"
                strokeWidth={0.5}
                style={{
                  default: { outline: 'none' },
                  hover: { outline: 'none', fill: '#D0D0D0' },
                  pressed: { outline: 'none' },
                }}
              />
            ))
          }
        </Geographies>

        {data.map((item, index) => {
          try {
            const metadata = item.metadata ? JSON.parse(item.metadata) : {};
            const lat = parseFloat(metadata.lat);
            const lng = parseFloat(metadata.lng);

            if (!lat || !lng) {
              console.warn(`Missing coordinates for ${item.xLabel}`);
              return null;
            }

            const value = parseFloat(item.yValue);

            // Calculate marker size (5-20 radius)
            let normalizedSize = 0.5;
            if (maxValue !== minValue) {
              normalizedSize = (value - minValue) / (maxValue - minValue);
            }
            // Calculate color (5 categories)
            // 0.0 - 0.2: Low (Green)
            // 0.2 - 0.4: Low-Mid (Light Green)
            // 0.4 - 0.6: Mid (Yellow)
            // 0.6 - 0.8: Mid-High (Orange)
            // 0.8 - 1.0: High (Red)
            const colors = [
              '#4CAF50', // Green
              '#8BC34A', // Light Green
              '#FFEB3B', // Yellow
              '#FF9800', // Orange
              '#F44336'  // Red
            ];
            
            // Size: Linear scale from 5px to 25px (20 steps)
            const radius = 5 + (normalizedSize * 20);

            const categoryIndex = Math.min(Math.floor(normalizedSize * 5), 4);
            const color = colors[categoryIndex];

            const tooltipText = `${item.xLabel}: ${metadata.currency || '$'}${value.toLocaleString()}${item.zValue ? ` (${item.zValue} txns)` : ''}`;

            return (
              <Marker key={index} coordinates={[lng, lat]}>
                <circle
                  r={radius}
                  fill={color}
                  fillOpacity={0.6}
                  stroke={color}
                  strokeWidth={2}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setTooltipContent(tooltipText)}
                  onMouseLeave={() => setTooltipContent('')}
                />
              </Marker>
            );
          } catch (err) {
            console.error(`Error rendering marker for ${item.xLabel}:`, err);
            return null;
          }
        })}
      </ComposableMap>

      {tooltipContent && (
        <Box
          position="absolute"
          top={10}
          left="50%"
          sx={{
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '14px',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          {tooltipContent}
        </Box>
      )}

      {/* Legend */}
      <Box
        position="absolute"
        bottom={10}
        right={10}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '12px',
        }}
      >
        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          Spending
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={15} height={15} bgcolor="#4CAF50" border="1px solid #ccc" />
          <Typography variant="caption">Low</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={15} height={15} bgcolor="#FFEB3B" border="1px solid #ccc" />
          <Typography variant="caption">Medium</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={15} height={15} bgcolor="#F44336" border="1px solid #ccc" />
          <Typography variant="caption">High</Typography>
        </Box>
      </Box>
    </Box>
  );
}
